import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const sections = [
  { title: '概要' },
  { title: '当社が収集することのある情報' },
  { title: 'バイオメトリック情報と権利放棄' },
  {
    title:
      'IDVサービスを本顧客に提供するために当社がお客様の個人情報を使用し開示する方法',
  },
  {
    title:
      'IDVサービスの維持、改善及び開発のために当社が匿名化情報又は集計情報を使用する方法',
  },
  { title: 'お客様の権利及び選択' },
  { title: 'セキュリティ及び保持' },
  { title: 'IDVプライバシー声明の更新' },
  { title: 'Plaidへのお問い合わせ' },
  { title: '欧州連合及び英国の居住者向け' },
];

const JaVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='idv-privacy-statement-ja'>IDVプライバシー声明</h4>

      <p>利用可能な言語:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>最終更新日: 2026年2月20日</p>

      <p>
        Plaid Inc.とその子会社であるPlaid Financial Ltd.及びPlaid,
        B.V.(以下、総称して「Plaid」又は「当社」といいます)は、企業、会社及び金融機関(以下、「本顧客」といいます)に対し、本人確認、詐欺検出、及びウォッチリストスクリーニングサービス(以下、「IDVサービス」又は「本サービス」といいます)をグローバルに提供しています。
      </p>

      <p>
        お客様は、当社のいずれかの本顧客から、IDVサービスを利用する様々なチェックを完了するよう求められる場合があります。このIDVプライバシー声明(以下、「IDVプライバシー声明」といいます)は、当社が本顧客に代わりIDVサービスを実施する際にお客様の個人データ(以下、「個人情報」といいます)がどのように使用されるかを説明しています。
      </p>

      <p>
        Plaidが18歳未満の消費者の個人情報を意図的に収集、処理、使用、又は共有することはありません。IDVサービスは、18歳以上の個人のみを対象としています。18歳未満の方は、IDVサービスを利用しないでください。
      </p>

      <p>
        当社がIDVサービスを本顧客に提供する場合、本顧客がお客様の個人情報を処理する目的及び手段を決定します(その場合、本顧客は、データ管理者とみなされます)。当社は、お客様の個人情報を処理する方法と理由に関しては、本顧客の指示に従います。Plaidは、本顧客の指示により本人確認、不正検知及びウォッチリストスクリーニングサービスを促進する場合、データ処理者又は副処理者となります。本顧客は、お客様の個人情報を収集するよう当社に指示します。Plaidは、お客様から情報(個人情報を含みます)を収集するよう指示された場合、処理者又は副処理者としての役割を果たします。Plaidは、個人情報を誰から収集するのか、及び個人情報をいつ収集して処理するのかについて、本顧客から指示を受けます。当社は、お客様の個人情報を使用して、IDVサービスを本顧客に提供します。また、当社は、本顧客の指示及び要請に従い、お客様から収集した情報(個人情報を含みます)について、本顧客に移転し、及び/又はダウンロードするためのアクセス権を提供します。
      </p>

      <p>
        お客様は常に、本顧客のプライバシーポリシーを確認し、お客様の個人情報に関する自らの権利行使について質問及び要請があれば本顧客宛に行うものとします。
      </p>

      <p>
        お客様は、IDVサービスの一部について、このポリシーに言及する当社ウェブサイト(総称して「本サイト」)を経由するなど、直接アクセスすることができます。例えば、お客様が本顧客であるか、IDVサービスに関するお問い合わせを希望される場合、又はお客様が当社のウェブサイトにアクセスされる場合、当社はデータ管理者となる場合があります。このような状況において、当社が個人データをどのように、またなぜ利用するのかについては、
        <Link href='https://plaid.com/legal/'>
          <a>www.plaid.com/legal/</a>
        </Link>
        にあるPlaidの該当するプライバシーポリシーに記載されています。
      </p>

      <p>
        PlaidがIDVサービスから取得した個人情報を第三者に販売することは決してありません。本顧客は、当社が個人情報を使用する方法、かつ本顧客それぞれのデータ保持ポリシーを管理する方法を決定するほか、個人情報を保持する期間及び当社が収集した情報(個人情報を含みます)を本顧客と共有するか否か、共有する場合はその時期を指定することができます。
      </p>

      <p>
        本顧客が要求するサービスに応じて、当社は本顧客に代わって個人情報を、IDVプライバシー声明に記載のとおり処理する場合があります。そのため、お客様には、IDVプライバシー声明を注意深くお読みになることをお勧めします。
      </p>

      <nav>
        <h5>セクションへジャンプ:</h5>
        <ol>
          {sections.map((section, index) => {
            return (
              <li key={index}>
                <a href={`#section-ja-${index + 1}`}>{`${section.title}`}</a>
              </li>
            );
          })}
        </ol>
      </nav>

      <div id='section-ja-1'>
        <h5>1. 概要</h5>
        <p>
          Plaidが本顧客にIDVサービスを提供する際に、お客様に対して、自らに関する情報を当社に提供するよう求められる場合があります。当社は本顧客に代わり、クラウドベースの本人確認、不正検知及びウォッチリストスクリーニングサービスを実施します。IDVサービスでは、お客様から得た情報及びお客様に関する情報を収集し、不正行為をリアルタイムで予測し、その防止を支援します。Plaidは次に、IDVサービスを完了させ、その結果を本顧客に直接提供します。当社の本顧客の要求に応じて、当社は、お客様から入手する情報(個人情報を含みます)の写しを、本顧客が使用するため、及びIDVサービスの一環として提供し、又は同情報を転送します。当社は、本顧客の指示に応じて当社がお客様から収集する情報(個人情報を含みます)を転送したり、当該情報をダウンロードするためのアクセス権を付与したりすることがあります。
        </p>
        <p>
          本顧客は、情報に対する要求の範囲と、IDVサービスを用いた分析に使用する可能性のあるお客様に関する個人情報の種類を決定します。本顧客は、IDVサービスを設定して、お客様の本人確認を行うために、特定の情報を要求し使用します。本顧客は、限られた情報のみを要求するようにIDVサービスを設定することができます。要求された情報が本人確認を行うには不十分である場合、本顧客の指示により、IDVサービスにおいて、本人確認プロセスを完了させるために追加情報が求められることがあります。
        </p>
        <p>
          お客様は、いずれかの処理の結果に納得しない場合、直接本顧客宛に問い合わせを行うものとします。
        </p>
      </div>

      <div id='section-ja-2'>
        <h5>2. 当社が収集することのある情報</h5>
        <p>
          本顧客が要求する本サービスに応じて、当社は、以下の方法でお客様の個人情報を収集することがあります。
        </p>
        <p>
          <strong>お客様が提供する情報:</strong>
          本顧客の要請でお客様が当社に情報を提供する際に、当社は個人情報を収集します。お客様が要求された情報を提供しないことを選択した場合、当社は、本顧客にIDVサービスを提供することができなくなります。本人確認及びスクリーニングに関する代替オプションがお客様の地域又は国籍で利用可能な場合は、本顧客に連絡し協議してください。
        </p>
        <p>
          本顧客に代わって要求される可能性のあるお客様に関する情報には、お客様の氏名、電子メールアドレス、住所、電話番号、生年月日などの連絡先情報、お客様の画像が添付された身分証明書、社会保障番号など政府によって割り当てられた身分証明書番号、及びお客様が身分証明書と一致し、かつ実在する人物に見えるかどうかを評価するための、お客様の写真又はビデオ画像が含まれます。
        </p>
        <p>
          当社は、JavaScriptコード、SDK、フォーム提出、画像収集、動画収集、及び本顧客が自身のウェブサイト又はモバイルアプリ(以下、「
          <strong>本顧客サイト</strong>
          」といいます)に組み込んだツールを用いた行動など、業界標準の手法を介してお客様から直接データを収集します。
        </p>
        <p>
          <strong>電話番号の確認に使用される情報:</strong>
          当社は、情報提供者がそのIDと紐づけられた電話番号を所有していることを確認するための措置を講じられるようにするため、本顧客に二要素認証機能を提供する場合もあります。この機能を本顧客のために提供する際に、当社は、本顧客又はお客様から提供された個人情報を使用して、テキストメッセージなどを介して検証コードをお客様に送信することがあります。お客様はその後、本顧客サイトの使用、又は本顧客での新規アカウント作成の際に、お客様の本人確認用にこのコードを入力することができます。
        </p>
        <p>
          <strong>
            お客様が本顧客サイトを訪問した際に、当社が自動的に収集する情報:
          </strong>
          IDVサービスは、特定の標準的な追跡技術を使用して、お客様が本顧客サイトとやり取りし、これを利用する際に、お客様のデバイスに関する特定の情報(IDVサービスに関するお客様の行動分析を含みます)、及びデバイスが本顧客サイト及びIDVサービスとやり取りするために使用される頻度を自動的に収集します。この情報は、例えば、お客様のIPアドレス及び特定の一意の識別子を含み、特定のコンピューター又はデバイスを識別し、一部の法域(EUなど)では個人情報とみなされる場合があります。
        </p>
        <p>
          当社が本顧客に代わって収集する情報の種類は、お客様が本顧客サイトを訪問する際に、APIインターフェース、SDKを使用するアプリ又はウェブページを介するかどうかによって異なり、以下が含まれます。
        </p>
        <ul>
          <li>
            <strong>ブラウザ及びデバイス情報:</strong>
            デバイスの種類とモデル、メーカー、オペレーティングシステムの種類とバージョン、ウェブブラウザの種類とバージョン、ユーザーエージェント、電話会社の名前と情報、タイムゾーン、ネットワーク接続の種類、IPアドレス、ハードウェアベースの識別子、ホスト名、デバイス識別子、Android/Google広告ID、Canvas
            Fingerprinting、お客様のデバイスが「ジェイルブレイク」されているか否かなどエミュレーション又はルート化に関連する特性、及びアプリの名前とバージョンを含みます。当社は、キャラクターセット、ホスト名、言語、ページのタイトル及びURL、リファラーURL、フォント数、フォントハッシュ、プラグイン数、プラグインハッシュ、画面の高さと幅、色深度、プラットフォーム、Cookieフットプリント、最大タッチポイント、Java対応、セッションストレージ、ローカルストレージ、解像度が改ざんされたか否か、言語又はOS、広告ブロックが有効であるか、Do
            Not
            Track機能が有効であるかの情報、フラッシュソケットIP及びフラッシュ識別子も収集します。SDKは、バッテリーレベル、デバイスのプロパティ、及び電話会社名など、電話関連のメタデータも収集します。
          </li>
          <li>
            <strong>本顧客のサイトでのお客様の行動に関する情報:</strong>
            すなわち、本顧客サイトでのお客様の活動に関する情報、セッションID、セッションの開始時刻及び停止時刻、タイムゾーンオフセット、及び位置情報(お客様のIPアドレスから推測される一般的な位置情報、又は状況によっては、緯度と経度座標に基づくより正確な位置情報)。お客様は、お客様の携帯機器のプレファレンスを変更することで、特定の本顧客サイトを通じた位置情報の収集を管理することができる場合があります。
          </li>
        </ul>
        <p>
          <strong>お客様に関する情報を収集する追跡技術の使用方法:</strong>
          当社は標準的な追跡技術を使用して、お客様が本顧客サイトを訪問する際、又は本顧客サイトとやり取りする際に、お客様のデバイス及びブラウザから特定の情報を自動的に収集します。お客様が本顧客サイトを介してIDVサービスを閲覧又は利用する場合、Plaidサーバーに通知され、当社は、ブラウザ又はアプリケーションから情報を収集することができます。さらに、当社は、JavaScript又はSDKなどの標準的な追跡技術を通じてお客様から直接データを収集します。本顧客は、この技術を自社のウェブサイトに組み込むことができます。
        </p>
        <p>
          当社が使用する可能性のある追跡技術の種類には、以下のものが含まれます。
        </p>
        <ul>
          <li>
            <strong>JavaScriptコード(JSスニペット)</strong>
            は、タグ又はピクセルとも呼ばれ、本顧客サイトのコンテンツに挿入されるコードの小さなスニペットです。ブラウザの設定を変更することで、JavaScriptを無効にすることができます。当該設定の変更方法に関する情報は、通常、お使いのインターネットブラウザプロバイダーのウェブサイトに掲載されています。Plaidは当該設定を管理していないため、提供されている情報を定期的に確認し、関連する変更がないか常に把握しておくことをお勧めします。
          </li>
          <li>
            <strong>モバイルSDK(ソフトウェア開発キット)</strong>
            、又は本顧客サイトに埋め込まれたコードブロックのうち、Plaidによる特定の情報の収集を可能にするもの。お客様は、お客様の携帯機器のオペレーティングシステムに適用される指示に従うことで、PlaidがSDKを通じて収集する特定の情報の使用を管理することができます。さらに、お客様の携帯機器のプレファレンスを変更することで、お客様の携帯機器が特定の本顧客サイトで位置情報を提供するかどうか、提供する場合はその方法を管理することができる場合もあります。Plaidは当該設定を管理していないため、提供されている情報を定期的に確認し、関連する変更がないか常に把握しておくことをお勧めします。
          </li>
          <li>
            <strong>Canvas Fingerprinting</strong>
            。これは、お客様のブラウザ上でHTML5
            Canvasのビルトイン機能からグラフィック画像の表示を可能にする追跡技術です。Canvas画像は通常、お客様のウェブブラウザのバージョン、オペレーティングシステム及びその設定、並びにインストールされたグラフィックスハードウェアなどの様々な要因により、異なるデバイス上において異なる様態で表示されるため、当社は、お客様と他のユーザーを区別することができます。Canvas
            Fingerprinting機能を無効にする方法として、JavaScriptの無効化、又はCanvas
            Fingerprinting手法をブロックするブラウザ拡張機能のダウンロードのいずれかを選択することができます。ただし、その場合、IDVサービスの機能が損なわれることがあります。Plaidは当該設定又は拡張機能を管理していないため、これらに関して提供されている情報を定期的に確認し、関連する変更がないか常に把握しておくことをお勧めします。
          </li>
          <li>
            <strong>Device Fingerprinting</strong>
            。これは、お客様のデバイスのソフトウェア及びハードウェアに関連する情報を使用して当社がお客様を識別することを可能にする追跡技術であり、お客様のデバイス情報と組み合わされたお客様のcanvas
            fingerprintのコンポーネントを含みます。Plaidは、詐欺行為や悪意のあるその他の者を阻止するため、及びサイバーセキュリティのために、デバイスのフィンガープリントを使用しています。
          </li>
        </ul>
        <p>
          お客様のIPアドレス及び特定の一意の識別子など、この情報の一部は、特定のコンピューター又はデバイスを特定する場合があるため、一部の法域(EUやカリフォルニア州など)では「個人情報」とみなされる場合があります。
        </p>
        <p>
          <strong>
            当社がサービスプロバイダー及び第三者の情報源から収集する情報:
          </strong>
          当社は、本顧客の要求に応じて、また本顧客に対するIDVサービスの一環として、当社が収集するお客様に関する情報と、当社がサービスプロバイダーから受領する情報を組み合わせる場合があります。例えば、当社は、IPアドレスが商用又は個人用であるか、電話番号が固定電話のものであるか、電子メールドメインが無料であるかなどの情報を受け取ることがあります。本顧客は、クレジットヘッダファイル、モバイルアカウント記録、ユーティリティ記録、自動車記録、その他の信頼できる情報源など、第三者の情報源に対して提供された情報と合致する第三者と協力するよう当社に求める場合があります。国によっては、お客様の氏名、住所、デバイスの詳細など、お客様の身元を確認する目的で、お客様の携帯電話会社はモバイルアカウントの詳細を開示するよう求められる場合があります。当社はまた、第三者からお客様に関する情報を受け取り、及び/又は一般に入手可能なお客様に関する情報を本顧客に代わって収集することがあります。
        </p>
        <p>
          <strong>特殊なカテゴリの個人データ:</strong>
          本顧客が、お客様の写真を含んでいる文書を当社宛に提供するようお客様に要求する場合、又はお客様が自らの写真若しくはビデオ画像を提供することにより身元を確認する必要がある場合、当該画像によって、お客様に関する特別なカテゴリのデータ(例えば健康情報(眼鏡を着用しているかなど)、人種若しくは民族、又は宗教若しくは政治的信条に関する情報)が明らかになることがあります。当社は、これらの特別なカテゴリの個人データを、本顧客の指示に従ってのみ、それぞれの本顧客のプライバシーポリシー及びIDVのプライバシー声明に従ってのみ使用します。その他の理由で利用することはありません。Plaid
          IDVが個人情報を販売することはありません。
        </p>
      </div>

      <div id='section-ja-3'>
        <h5>3. バイオメトリック情報と権利放棄</h5>
        <p>
          当社の本顧客が、お客様に対し、ご自分の顔の画像及び写真又はビデオ画像(自撮り画像と呼ばれることもあります)を掲載している身分証明書を当社に提供するよう求めた場合、当社は、身分証明書の画像から導き出されたお客様の顔の形状を、お客様が提供した写真又はビデオ画像に写っているお客様の顔の形状と比較して、身分証明書と一致するかどうかを判断することがあります。また、当社は、ビデオ画像が実在する人間のものであるかどうかの判断、及び該当する場合はお客様の推定年齢を判断することを含め、不正行為を検出及び防止するために、お客様の顔面形状のスキャンデータを使用する場合があります。お客様の身分証明書画像、写真、又はビデオ画像、及びそれらの画像から抽出された顔面形状のスキャンデータは、法域によってはバイオメトリック情報とみなされる場合があります。
        </p>
        <p>
          お客様は、IDVサービスを使用して当社の本顧客が提供するアプリ又はサービスに接続することにより、
          <Link href='#biometric-policy-and-release'>
            <a>Plaidのバイオメトリックポリシー及び権利放棄</a>
          </Link>
          を読み、理解し、自発的に同意したことになり、お客様の画像、写真、又はビデオ画像(顔面形状のスキャンデータを作成するためにこれらの画像から抽出されたデータを含みます)の収集、処理、使用、開示、又は共有に関連するいかなる請求権についても、Plaid及びPlaidのサービスプロバイダーのためにこれを放棄するものとします。
        </p>
        <p>
          お客様の身分証明書、写真、又はビデオ画像からの当社による画像の収集、使用、処理、開示、及び保持(お客様の地域でバイオメトリックデータとみなされる可能性のある、これらの画像から抽出された顔面形状のスキャンデータを含みます)に関する詳細については、
          <Link href='#biometric-policy-and-release'>
            <a>Plaidのバイオメトリックポリシー及び権利放棄</a>
          </Link>
          をご覧ください。
        </p>
      </div>

      <div id='section-ja-4'>
        <h5>
          4.
          IDVサービスを本顧客に提供するために当社がお客様の個人情報を使用し開示する方法
        </h5>
        <p>
          本顧客は、IDVサービスを使用する場合があり、当社は、情報提供者が本人であるかの蓋然性について本顧客に情報を返送します。当社はまた、本顧客の指示及び要求に応じて当社がお客様から収集する情報(お客様の個人情報を含みます)を、本顧客に移転したり、当該情報をダウンロードするためのアクセス権を付与したりします。
        </p>
        <p>
          Plaidは、お客様の個人情報を使用して、IDVサービスを本顧客に提供し、本顧客が各自の法的義務を遵守できるように支援します。当社は、IDVサービスにおける不正行為を特定し防止するために、デバイス情報又は追跡技術を使用する場合があります。例えば、当社は、当社のクラウドベースの本人確認サービスを通じてお客様の個人情報を処理し、本顧客サイト上の特定のイベント又は活動に向けて、マッチ情報を本顧客に返送します。さらに、本顧客が二要素認証を使用する場合、当社は、テキストメッセージでお客様に認証コードを送信するために、お客様の電話番号などの個人情報を処理します。これにより、この機能を利用する本顧客は、IDに関連付けられた電話番号の所有を確認することで、IDを検証できます。
        </p>
      </div>

      <div id='section-ja-5'>
        <h5>
          5.
          IDVサービスの維持、改善及び開発のために当社が匿名化情報又は集計情報を使用する方法
        </h5>
        <p>
          当社は、IDVサービスを維持、改善、最適化、又は開発するために、情報の匿名化、非特定化又は集計を行う場合があり、また、IDVサービスにおける不正行為を防止及び検出するために、匿名化、非特定化、又は集計済みの情報を使用することがあります。Plaid
          IDVが、IDVサービスの維持、改善、最適化又は開発のためにお客様の個人情報を使用することはありません。
        </p>
      </div>

      <div id='section-ja-6'>
        <h5>6. お客様の権利及び選択</h5>
        <p>
          お客様の所在地によって異なりますが、適用法に従い、お客様は、当社が本顧客に代わって処理するお客様の個人情報に関する権利を有する場合があります。
        </p>
        <p>
          <strong>データ保護の権利:</strong>
          当社が本顧客に代わってデータ処理者、副処理者又はサービスプロバイダーとしての役割を果たす場合、お客様は、ご自身の個人情報へのアクセス又はご自身のデータ保護権の行使を希望する場合、その要求を本顧客に対して行わなければなりません。当社は、本顧客がお客様の要求に対応する際に支援し、本顧客の指示に従って行動します。
        </p>
        <p>
          当社がデータ管理者として個人データの処理に責任を負う場合、当社がどのように要求に対処するかについての詳細は、
          <Link href='https://plaid.com/legal/#consumers'>
            <a>https://plaid.com/legal/#consumers</a>
          </Link>
          にあるPlaidのエンドユーザープライバシーポリシーをご覧ください。
        </p>
      </div>

      <div id='section-ja-7'>
        <h5>7. セキュリティ及び保持</h5>
        <p>
          <strong>セキュリティ保護措置:</strong>
          当社は、IDVサービスの一環として処理される個人情報を不正なアクセス、開示、改ざん及び破壊から保護するための技術的及び組織的なセキュリティ対策を講じます。
        </p>
        <p>
          <strong>データ保持:</strong>
          本顧客は、各自のデータ保持ポリシーを管理しており、お客様の個人情報を保持する期間を指定することがあります。本顧客から指示を受けた場合、お客様の個人情報は、削除又は匿名化されます。
        </p>
      </div>

      <div id='section-ja-8'>
        <h5>8. IDVプライバシー声明の更新</h5>
        <p>
          IDVプライバシー声明は、IDVサービスに対する本顧客の要求事項、及び変化する法律上、技術上又は事業上の進展に対応するために随時改訂されることがあります。更新情報があれば本サイトで提供され、改訂版は、掲載された時点で効力を生じます。当社は、お客様から以前に収集した個人情報の使用方法又は共有方法に重大な変更を加える場合、更新版をここに掲載します。IDVプライバシー声明の最終更新日は、このページの上部に表示されている「最終更新日」又は「発効日」で確認することができます。
        </p>
      </div>

      <div id='section-ja-9'>
        <h5>9. Plaidへのお問い合わせ</h5>
        <p>
          IDVプライバシー声明又は当社のプライバシー慣行についてご質問やご意見がございましたら、
          <Link href='mailto:privacy@plaid.com'>
            <a>privacy@plaid.com</a>
          </Link>
          又は郵送にてPlaidまでご連絡ください。連絡先は以下のとおりです。
        </p>
        <p>
          Plaid Inc.
          <br />
          Attn: Legal
          <br />
          PO Box 7775 #35278
          <br />
          San Francisco, California 94120-7775
          <br />
          U.S.A.
        </p>
      </div>

      <div id='section-ja-10'>
        <h5>10. 欧州連合及び英国の居住者向け</h5>
        <p>
          Plaidは、EU/EEAにおけるGDPR及び英国における2018年データ保護法(その後の改正を含む)において、Plaid
          B.V.をデータ保護担当者として任命しています。お客様がEU/EEA又は英国の居住者であり、当社が個人情報を処理した、又は処理している場合、お客様はGDPRに基づく権利を行使することができます。
        </p>
        <p>
          当社が本顧客に代わってお客様の個人情報を処理した場合において、当該本顧客がEEA又は英国に所在している場合も、個人情報に関する連絡は、Plaidのデータ保護担当者ではなく、本顧客宛に行ってください。それでもなお、Plaid宛に質問することを希望する場合、又は別途個人情報に関する権利の行使を希望する場合は、以下のいずれかの方法で行うことができます。
        </p>
        <ul>
          <li>privacy@plaid.com宛に電子メールを送信する。</li>
          <li>
            当社のデータ保護担当者まで、
            <Link href='https://plaid.com/legal/data-protection-request-form/'>
              <a>https://plaid.com/legal/data-protection-request-form/</a>
            </Link>
            のウェブフォームから連絡する。
          </li>
        </ul>
        <p>
          Plaidに対し以下の住所宛てに問い合わせ内容を郵送することもできます。
        </p>
        <p>
          <em>英国在住の場合:</em>
        </p>
        <p>
          Plaid Financial Ltd.
          <br />
          Attn: Legal
          <br />
          Techspace Level 4, 25 Worship Street
          <br />
          London, United Kingdom, EC2A 2DX
        </p>
        <p>
          <em>EEAに在住の場合:</em>
        </p>
        <p>
          Plaid B.V.
          <br />
          Attn: Legal
          <br />
          Muiderstraat 1<br />
          1011PZ Amsterdam
          <br />
          The Netherlands
        </p>
        <p>
          お客様から通信を受けた場合、Plaidは、お客様の個人情報がお客様以外の誰にも提供されないよう徹底するためにお客様の身元を証明する証拠書類の提示を求める場合があります。
        </p>
      </div>

      <hr className='section-divider' />
    </>
  );
};

export default JaVersion;
